import { TRIPS_PATH } from "@b2bportal/core-utilities";
import {
  buildStayTripCancelUrl,
  buildStayTripDetailsUrl,
  PTN_COMMUNITY_ID,
  PTN_PERKSPOT_USER_ID,
} from "./urls";

/**
 * Gates the b2bportal side of serving My Trips from commerce-portals. Must
 * stay paired with the `/trips` list route in
 * deployment/b2bportal_perkspot/workloads/perkspot/BUILD. Read at call time
 * so tests can stub it (ATL-1503).
 */
export const isCommerceTripsEnabled = (): boolean =>
  import.meta.env.VITE_COMMERCE_TRIPS_ENABLED === "true";

export const buildCommerceTripsUrl = ({
  perkspotUserId,
  communityId,
}: {
  perkspotUserId?: string;
  communityId?: number;
} = {}): string => {
  const searchParams = new URLSearchParams();

  if (perkspotUserId != null) {
    searchParams.set(PTN_PERKSPOT_USER_ID, perkspotUserId);
  }
  if (communityId != null) {
    searchParams.set(PTN_COMMUNITY_ID, `${communityId}`);
  }

  const query = searchParams.toString();
  const base = `${import.meta.env.VITE_COMMERCE_PORTAL_BASE_URL ?? ""}${TRIPS_PATH}`;
  return query ? `${base}?${query}` : base;
};

/**
 * Full document load, so the gateway (not React Router) picks the app that
 * serves the path.
 */
export const hardNavigate = (
  url: string,
  mode: "assign" | "replace" = "assign"
): void => {
  window.location[mode](url);
};

export const COMMERCE_TRIPS_RELOAD_GUARD_KEY = "perkspot:commerce-trips-reload";
export const COMMERCE_TRIPS_RELOAD_GUARD_MS = 10_000;

/**
 * Guards the `/trips/*` safety-net reload against a loop. If b2bportal is
 * rendering the same URL again right after reloading it, the gateway still
 * routes `/trips` here, so the caller should render the legacy app instead.
 */
export const shouldReloadToCommerce = (
  href: string,
  now: number = Date.now()
): boolean => {
  try {
    const raw = sessionStorage.getItem(COMMERCE_TRIPS_RELOAD_GUARD_KEY);
    const last = raw
      ? (JSON.parse(raw) as { href?: string; at?: number })
      : null;
    if (
      last?.href === href &&
      typeof last.at === "number" &&
      now - last.at <= COMMERCE_TRIPS_RELOAD_GUARD_MS
    ) {
      return false;
    }
  } catch {
    // Unreadable or corrupt guard: fall through and allow the reload.
  }
  try {
    sessionStorage.setItem(
      COMMERCE_TRIPS_RELOAD_GUARD_KEY,
      JSON.stringify({ href, at: now })
    );
  } catch {
    // Storage unavailable: fail closed and skip the reload rather than risk
    // an unguarded loop.
    return false;
  }
  return true;
};

const LEGACY_TRIP_DETAILS = /^\/trips\/([^/]+)\/?$/;
const LEGACY_HOTEL_CANCEL = /^\/trips\/hotel\/cancel\/([^/]+)\/?$/;

const decodeSegment = (segment: string): string | null => {
  try {
    return decodeURIComponent(segment);
  } catch {
    return null;
  }
};

/**
 * Legacy b2bportal trip URLs that PerkSpot's own sites still link to,
 * mapped to their commerce-portals equivalents (ATL-1503). Returns null for
 * anything else, including the trips list and `/trips/lookup`, which the
 * gateway already serves from commerce.
 */
export const resolveLegacyTripRedirect = (
  pathname: string,
  attribution: { perkspotUserId?: string; communityId?: number } = {}
): string | null => {
  const cancel = LEGACY_HOTEL_CANCEL.exec(pathname);
  if (cancel) {
    const reservationId = decodeSegment(cancel[1]);
    return reservationId
      ? buildStayTripCancelUrl({ reservationId, ...attribution })
      : null;
  }

  const details = LEGACY_TRIP_DETAILS.exec(pathname);
  if (details && details[1] !== "lookup" && details[1] !== "hotel") {
    const reservationId = decodeSegment(details[1]);
    return reservationId
      ? buildStayTripDetailsUrl({ reservationId, ...attribution })
      : null;
  }

  return null;
};
